import { UNIT_ALL } from "@/components/shared/util";
import { parseServerTime } from "@/filters/DateAndTime";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { userNamespace } from "@/store/modules/user";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { Colors } from "./util";

@Component({})
export default class NetworkTest extends Vue {
  @Action("wirelessTestStart", userNamespace)
  wirelessTestStart!: Function;

  @Action("wirelessTestNote", userNamespace)
  wirelessTestNote!: Function;

  @Action("wirelessTestStop", userNamespace)
  wirelessTestStop!: Function;

  @Action("wirelessTestResult", userNamespace)
  wirelessTestResult!: Function;

  @Getter("isTesting", patientNamespace)
  isTesting!: boolean;

  @Action("setIsTesting", patientNamespace)
  setIsTesting!: Function;

  @Getter("singlePatientView", patientNamespace)
  singlePatientView!: boolean;

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  @Prop(Boolean)
  helpOpen!: boolean;

  @Prop(Boolean)
  resultsOpen!: boolean;

  @Getter("popupDescription", patientNamespace)
  popupDescription!: string;

  @Getter("area", userNamespace)
  area!: string;

  isHelpOpen: boolean = false;

  isInfoOpen: boolean = false;
  isResultsOpen: boolean = false;

  noteText = "";

  showThankYou = false;

  testResults: any = {
    notes: [],
    serverInfo: {},
    summary: {},
    scoreCard: {
      isPassing: false,
      statusExplanation: "",
      isPassingFidelity: false,
      isPassingLatency: false,
      isPassingSecurity: false
    },
    testerInfo: {},
    tests: [],
    timespanString: ""
  };

  get isPassing() {
    return (
      this.testResults != null &&
      this.testResults.scoreCard != null &&
      this.testResults.scoreCard.isPassing != null &&
      this.testResults.scoreCard.isPassing === true
    );
  }

  get hasResults() {
    return (
      this.testResults != null &&
      this.testResults.summary != null &&
      ((this.testResults.summary.testCount != null &&
        this.testResults.summary.testCount > 1) ||
        this.testResults.notes != null)
    );
  }

  get statusExplanation() {
    return this.testResults != null &&
      this.testResults.scoreCard != null &&
      this.testResults.scoreCard.statusExplanation != null
      ? this.testResults.scoreCard.statusExplanation
      : "";
  }

  get isPassingFidelity() {
    return (
      this.testResults != null &&
      this.testResults.scoreCard != null &&
      this.testResults.scoreCard.isPassingFidelity != null &&
      this.testResults.scoreCard.isPassingFidelity === true
    );
  }

  get isPassingLatency() {
    return (
      this.testResults != null &&
      this.testResults.scoreCard != null &&
      this.testResults.scoreCard.isPassingLatency != null &&
      this.testResults.scoreCard.isPassingLatency === true
    );
  }

  get isPassingSecurity() {
    return (
      this.testResults != null &&
      this.testResults.scoreCard != null &&
      this.testResults.scoreCard.isPassingSecurity != null &&
      this.testResults.scoreCard.isPassingSecurity === true
    );
  }

  get showingPopup() {
    return this.$route.query.popupName;
  }

  get testIconColor() {
    return this.isTesting ? Colors.Red : Colors.TestGreen;
  }

  get colorRed() {
    return Colors.Red;
  }

  get colorGreen() {
    return Colors.TestGreen;
  }

  get colorBlue() {
    return Colors.LogoBlue;
  }

  get testIcon() {
    return this.isTesting ? "portable_wifi_off" : "network_check";
  }

  get testText() {
    return (this.isTesting ? "Stop" : "Start") + " test";
  }

  get testMessage() {
    return this.isTesting ? "Test session Active" : "";
  }

  summaryConfig = [
    { key: "testCount", value: "Count" },
    { key: "testsIncomplete", value: "Incomplete" },
    { key: "passPercentString", value: "Passed" },
    { key: "latencyString", value: "Latency (avg/max)" },
    { key: "bytesString", value: "Size (avg/max)" }
  ];

  get displaySummary() {
    return this.testResults.summary.filter(
      (f: any) => this.summaryText(f.key) != null
    );
  }

  summaryText(key: string) {
    const summ = this.summaryConfig.filter(f => f.key == key);
    return summ.length > 0 && summ[0].value != null ? summ[0].value : "";
  }

  toggleHelpOpen() {
    this.isHelpOpen = !this.isHelpOpen;
  }

  toggleInfoOpen() {
    this.isInfoOpen = !this.isInfoOpen;
  }

  toggleResultsOpen() {
    this.isResultsOpen = !this.isResultsOpen;
  }

  async addNote() {
    if (this.noteText != null && this.noteText !== "") {
      this.wirelessTestNote(this.noteText);
      this.testResults = await this.wirelessTestResult();
      if (!this.resultsOpen) this.isResultsOpen = true;
      this.noteText = "";
    }
  }

  async toggleWirelessTest() {
    if (this.isTesting) {
      this.wirelessTestStop();
      this.setIsTesting(false);
      this.showThankYou = true;
    } else {
      this.wirelessTestStart(this.noteText);
      this.setIsTesting(true);
      this.testResults = this.wirelessTestResult();
      if (!this.resultsOpen) this.isResultsOpen = this.hasResults;
    }
  }

  async mounted() {
    if (this.isTesting == true) {
      this.testResults = await this.wirelessTestResult();
    }

    if (this.helpOpen) this.isHelpOpen = true;
    if (!this.resultsOpen) this.isResultsOpen = this.hasResults;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  displayTime(time: string) {
    const t = parseServerTime(time.replace(" ", "T").split(".")[0]);
    return (
      t.format(!this.isMobile ? "MMM M, YYYY" : "M/D/YY") +
      " " +
      t.format(!this.isMobile ? "hh:mm.ss A" : "h:mm.ss a")
    );
  }

  @Getter("censusPreferences", navigationNamespace)
  censusPreferences!: any;

  navigateToCensus() {
    // TODO: how much is still needed
    const context =
      this.area !== "" ? this.area : this.$route.path.split("/")[1];
    const params: any = { area: context, unit: UNIT_ALL };
    const savedPrefs = this.censusPreferences[this.area];
    if (savedPrefs) {
      Object.assign(params, savedPrefs);
    }

    this.$router.push({
      name: "census",
      params
    });
  }
}
